import { useEffect, useState } from 'react'
import { Cloud, CloudOff, RefreshCw, AlertTriangle, LogOut, Receipt, ShoppingCart, Database, CircleCheck, HelpCircle } from 'lucide-react'
import { pendingCount, flush } from '../outbox'
import { clearUser } from '../db'

/**
 * Session/status chrome for the POS screen, in two variants:
 *
 *   variant="bar"  (default) — the slim strip pinned to the top on phones.
 *   variant="rail" — a vertical sidebar for laptop/desktop widths. Renders
 *                    with `hidden lg:flex`, so it only exists on large
 *                    screens; the bar should be wrapped in `lg:hidden`.
 *
 * Both communicate the same three things at a glance:
 *   - Are we online? (browser network state)
 *   - How fresh is the cached drug snapshot?
 *   - How many sales are queued to upload?
 */
export default function SyncStatusBar({ snapshotAt, pharmacyName, user, onLoggedOut, onOpenReceipts, onOpenHelp, variant = 'bar', onFocusSearch }) {
  const [online, setOnline] = useState(navigator.onLine)
  const [pending, setPending] = useState(0)
  const [flushing, setFlushing] = useState(false)
  const [snapshotAge, setSnapshotAge] = useState(formatAge(snapshotAt))
  const [announcement, setAnnouncement] = useState('')

  useEffect(() => {
    // Announced once per change through a polite live region, so screen
    // reader users hear it without being interrupted on a flaky connection.
    const onUp = () => { setOnline(true); setAnnouncement('Back online. Waiting sales will upload now.') }
    const onDown = () => { setOnline(false); setAnnouncement('You are offline. Sales will be saved on this device.') }
    window.addEventListener('online', onUp)
    window.addEventListener('offline', onDown)
    return () => {
      window.removeEventListener('online', onUp)
      window.removeEventListener('offline', onDown)
    }
  }, [])

  useEffect(() => {
    const tick = async () => {
      setPending(await pendingCount())
      setSnapshotAge(formatAge(snapshotAt))
    }
    tick()
    const id = setInterval(tick, 15_000)
    return () => clearInterval(id)
  }, [snapshotAt])

  const handleFlush = async () => {
    setFlushing(true)
    try { await flush() } finally { setFlushing(false) }
    setPending(await pendingCount())
  }

  const handleLogout = async () => {
    if (!window.confirm('Sign out? Pending sales will stay queued for the next sign-in.')) return
    await clearUser()
    onLoggedOut?.()
  }

  const stale = isStale(snapshotAt)

  if (variant === 'rail') {
    return (
      <aside className="pc-register-rail hidden lg:flex shrink-0 flex-col bg-brand-800 text-white">
        {/* Brand */}
        <div className="flex items-center gap-3 px-4 pt-5 pb-4">
          <BrandMark size={38} />
          <div className="min-w-0">
            <p className="text-sm font-bold leading-tight truncate">{pharmacyName || 'PharmaCare'}</p>
            <p className="text-xs text-brand-100/75 mt-0.5">Offline register</p>
          </div>
        </div>

        {/* Nav */}
        <nav className="px-3 space-y-1">
          <button
            onClick={onFocusSearch}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/10 text-white text-sm font-semibold"
          >
            <ShoppingCart size={16} /> Register
          </button>
          {onOpenReceipts && (
            <button
              onClick={onOpenReceipts}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-brand-100/80 hover:bg-white/5 hover:text-white text-sm font-semibold transition-colors"
            >
              <Receipt size={16} /> Receipts
            </button>
          )}
          {onOpenHelp && (
            <button
              onClick={onOpenHelp}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-brand-100/80 hover:bg-white/5 hover:text-white text-sm font-semibold transition-colors"
            >
              <HelpCircle size={16} /> Help
            </button>
          )}
        </nav>
        <p className="sr-only" role="status" aria-live="polite">{announcement}</p>

        {/* Status */}
        <div className="mt-6 px-4">
          <p className="text-xs font-bold uppercase tracking-widest text-brand-100/70 mb-2">Connection status</p>
          <div className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-3 space-y-2.5">
            <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${online ? 'text-emerald-300' : 'text-amber-300'}`}>
              {online ? <CircleCheck size={12} /> : <CloudOff size={12} />}
              {online ? 'Cloud connected' : 'Offline register'}
            </p>
            <p className={`flex items-center gap-2 text-xs ${stale ? 'text-rose-300' : 'text-brand-100/70'}`}>
              <Database size={11} /> Stock saved {snapshotAge}
              {stale && <AlertTriangle size={10} className="inline ml-1 -mt-0.5" />}
            </p>
            <p className="flex items-center gap-2 text-xs text-brand-100/70">
              <RefreshCw size={11} /> {pending ? `${pending} sale${pending === 1 ? '' : 's'} awaiting upload` : 'All sales uploaded'}
            </p>
            {pending > 0 && (
              <button
                onClick={handleFlush}
                disabled={flushing}
                className="w-full flex items-center justify-center gap-1.5 mt-1 px-2 py-1.5 rounded-lg bg-amber-400/15 text-amber-200 ring-1 ring-amber-300/30 text-xs font-semibold"
              >
                <RefreshCw size={11} className={flushing ? 'animate-spin' : ''} />
                Upload pending now
              </button>
            )}
          </div>
        </div>

        <div className="flex-1" />

        {/* User + sign out */}
        <div className="px-4 pb-4 pt-3 border-t border-white/10">
          <p className="text-[12px] font-semibold text-white truncate">{user?.full_name || user?.username}</p>
          <p className="text-xs text-brand-100/75 truncate mb-2.5">{user?.role} · This device</p>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-brand-100/70 hover:bg-white/5 hover:text-white text-xs font-semibold transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
    )
  }

  return (
      <header className="pc-mobile-register-header shrink-0">
      {/* Brand identity band */}
      <div
        className="bg-brand-800 text-white"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex items-center justify-between px-3.5 py-2.5 gap-2">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <BrandMark size={32} />
            <div className="min-w-0">
              <p className="text-[13px] font-bold text-white truncate leading-tight">{pharmacyName || 'PharmaCare'}</p>
              <p className="text-xs text-brand-100/70 truncate">
                Offline register · {user?.full_name || user?.username}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {onOpenHelp && (
              <button
                onClick={onOpenHelp}
                className="h-11 w-11 grid place-items-center rounded-lg text-brand-100/80 active:bg-white/10 active:text-white"
                title="Help"
                aria-label="Help"
              >
                <HelpCircle size={18} />
              </button>
            )}
            {onOpenReceipts && (
              <button
                onClick={onOpenReceipts}
                className="h-11 w-11 grid place-items-center rounded-lg text-brand-100/80 active:bg-white/10 active:text-white"
                title="Receipts"
                aria-label="Past receipts"
              >
                <Receipt size={18} />
              </button>
            )}
            <button
              onClick={handleLogout}
              className="h-11 w-11 grid place-items-center rounded-lg text-brand-100/80 active:bg-white/10 active:text-white"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </div>

      <p className="sr-only" role="status" aria-live="polite">{announcement}</p>

      {/* Live status strip */}
      <div className={`pc-continuity-strip flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider border-b ${
        online ? 'bg-brand-50 text-brand-700 border-brand-100' : 'bg-amber-50 text-amber-700 border-amber-100'
      }`}>
        {online ? <Cloud size={12} /> : <CloudOff size={12} />}
        <span>{online ? 'Connected' : 'Offline register'}</span>
        <span className="text-sage-300 font-normal normal-case tracking-normal">·</span>
        <span className={stale ? 'text-rose-600' : 'text-sage-500'}>
          Stock {snapshotAge}
        </span>
        {pending > 0 && (
          <>
            <span className="text-sage-300 font-normal normal-case tracking-normal">·</span>
            <button
              onClick={handleFlush}
              disabled={flushing}
              className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 normal-case tracking-normal text-xs font-semibold ring-1 ring-amber-200"
            >
              <RefreshCw size={10} className={flushing ? 'animate-spin' : ''} />
              {pending} to upload
            </button>
          </>
        )}
        {pending === 0 && stale && (
          <span className="ml-auto flex items-center gap-1 text-rose-600 normal-case tracking-normal text-xs font-normal">
            <AlertTriangle size={10} /> snapshot is old
          </span>
        )}
      </div>
    </header>
  )
}

/** The real PharmaCare app icon — same mark as the desktop app, marketing
 *  site, and installed PWA icon. */
function BrandMark({ size = 32 }) {
  return (
    <span
      className="shrink-0 rounded-[10px] overflow-hidden bg-white shadow-sm shadow-black/20 grid place-items-center"
      style={{ width: size, height: size }}
    >
      <img
        src={`${import.meta.env.BASE_URL}icon-192.png`}
        alt="PharmaCare PMS logo"
        className="w-full h-full object-cover"
      />
    </span>
  )
}

function formatAge(iso) {
  if (!iso) return 'never'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'never'
  const diff = Date.now() - then
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`
  return `${Math.floor(diff / 86_400_000)} d ago`
}

function isStale(iso) {
  if (!iso) return true
  const STALE_MS = 7 * 24 * 60 * 60 * 1000   // 7 days, per DESIGN_PWA.md §7.5
  return Date.now() - new Date(iso).getTime() > STALE_MS
}
